import { TypeManagerDecorator } from "../../../main/type.map.service";
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, ViewEncapsulation } from "@angular/core";
import { CommonModule } from "@angular/common";
import { s25Resource } from "../../../util/s25.resource";
import { ENGAGEMENT_LABELS } from "./engagement.labels";
import { EngagementBadge, EngagementBadgeEarned, EngagementService } from "./engagement.service";
import { S25FilestoreImageComponent } from "../../../standalone/s25.filestore.image.component";
import { UserStore } from "../../../stores/user.store";

type EarnedRow = {
    earned: EngagementBadgeEarned;
    def?: EngagementBadge;
    isSecret: boolean;
    displayName: string;
};

@TypeManagerDecorator("s25-ng-se-badge-notification")
@Component({
    selector: "s25-ng-se-badge-notification",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [CommonModule, S25FilestoreImageComponent],
    template: `
        @if (overlayOpen()) {
            <div class="se-badge-notify__backdrop" role="presentation"></div>
            <div
                class="se-badge-notify__panel se-badge-notify__panel--animate"
                role="dialog"
                aria-modal="true"
                aria-labelledby="se-badge-notify-title"
            >
                <button type="button" class="se-badge-notify__dismiss" (click)="dismiss()" aria-label="Dismiss">
                    ×
                </button>
                <h2 id="se-badge-notify-title" class="se-badge-notify__title se-badge-notify__shimmer">
                    {{ headline() }}
                </h2>
                <div class="se-badge-notify__grid">
                    @for (row of displayRows(); track row.earned.earnedId) {
                        <div class="se-badge-notify__card se-badge-notify__glow">
                            @if (row.def?.iconUrl) {
                                <s25-ng-filestore-image
                                    class="se-badge-notify__icon"
                                    [value]="row.def.iconUrl"
                                    [readOnly]="true"
                                />
                            } @else {
                                <div
                                    class="se-badge-notify__icon se-badge-notify__icon--placeholder"
                                    aria-hidden="true"
                                >
                                    ★
                                </div>
                            }
                            <div class="se-badge-notify__name">{{ row.displayName }}</div>
                            @if (row.isSecret) {
                                <div class="se-badge-notify__secret-tag">Secret</div>
                            }
                        </div>
                    }
                </div>
                <button type="button" class="se-badge-notify__ok aw-button aw-button--primary" (click)="dismiss()">
                    Awesome
                </button>
            </div>
        }
    `,
    styles: [
        `
            :host {
                display: contents;
            }
            .se-badge-notify__backdrop {
                position: fixed;
                inset: 0;
                z-index: 10040;
                background: rgba(15, 23, 42, 0.55);
                backdrop-filter: blur(3px);
                animation: se-badge-notify-fade-in 0.35s ease;
            }
            .se-badge-notify__panel {
                position: fixed;
                z-index: 10050;
                left: 50%;
                top: 50%;
                transform: translate(-50%, -50%);
                width: min(92vw, 640px);
                max-height: min(85vh, 600px);
                overflow-x: hidden;
                overflow-y: auto;
                padding: 1.75rem 1.5rem 1.5rem;
                border-radius: 16px;
                background: linear-gradient(160deg, #1e293b 0%, #0f172a 55%, #172554 100%);
                color: #f8fafc;
                box-shadow:
                    0 0 0 1px rgba(255, 255, 255, 0.08),
                    0 25px 50px -12px rgba(0, 0, 0, 0.55),
                    0 0 80px rgba(99, 102, 241, 0.25);
                animation: se-badge-notify-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
            }
            .se-badge-notify__panel--animate {
                animation: se-badge-notify-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
            }
            .se-badge-notify__dismiss {
                position: absolute;
                top: 0.5rem;
                right: 0.65rem;
                width: 2.25rem;
                height: 2.25rem;
                border: none;
                border-radius: 999px;
                background: rgba(255, 255, 255, 0.1);
                color: #e2e8f0;
                font-size: 1.5rem;
                line-height: 1;
                cursor: pointer;
            }
            .se-badge-notify__dismiss:hover {
                background: rgba(255, 255, 255, 0.18);
            }
            .se-badge-notify__title {
                margin: 0 2rem 0.5rem 0;
                font-size: 1.35rem;
                font-weight: 700;
                letter-spacing: 0.02em;
            }
            .se-badge-notify__shimmer {
                background: linear-gradient(110deg, #f8fafc 0%, #c7d2fe 35%, #fde68a 50%, #c7d2fe 65%, #f8fafc 100%);
                background-size: 200% auto;
                -webkit-background-clip: text;
                background-clip: text;
                color: transparent;
                animation: se-badge-notify-shimmer 2.5s linear infinite;
            }
            .se-badge-notify__secret {
                margin: 0 0 0.35rem;
                font-size: 0.95rem;
                color: #fde68a;
                font-weight: 600;
            }
            .se-badge-notify__sub {
                margin: 0 0 1.25rem;
                font-size: 0.9rem;
                color: #94a3b8;
            }
            .se-badge-notify__grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
                gap: 1rem;
                margin-bottom: 1.5rem;
            }
            .se-badge-notify__card {
                text-align: center;
                padding: 0.75rem 0.5rem;
                border-radius: 12px;
                background: rgba(255, 255, 255, 0.06);
                border: 1px solid rgba(255, 255, 255, 0.1);
                overflow: hidden;
                min-width: 0;
            }
            .se-badge-notify__glow {
                animation: se-badge-notify-card-glow 2s ease-in-out infinite alternate;
            }
            .se-badge-notify__icon {
                margin: 0 auto 0.5rem;
                display: block;
                width: 64px;
                height: 64px;
                overflow: hidden;
            }
            .se-badge-notify__icon img {
                width: 64px;
                height: 64px;
                object-fit: contain;
                border-radius: 10px;
                border: none;
                filter: drop-shadow(0 4px 12px rgba(129, 140, 248, 0.45));
            }
            .se-badge-notify__icon--placeholder {
                width: 64px;
                height: 64px;
                margin: 0 auto 0.5rem;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 12px;
                background: linear-gradient(145deg, #4c1d95, #312e81);
                font-size: 1.75rem;
                font-weight: 700;
                color: #fde68a;
                box-shadow: 0 0 24px rgba(167, 139, 250, 0.5);
            }
            .se-badge-notify__secret-tag {
                margin-top: 0.3rem;
                font-size: 0.68rem;
                text-transform: uppercase;
                letter-spacing: 0.08em;
                font-weight: 700;
                color: #fde68a;
                background: rgba(167, 139, 250, 0.2);
                border-radius: 4px;
                padding: 0.1rem 0.45rem;
            }
            .se-badge-notify__name {
                font-size: 0.82rem;
                font-weight: 600;
                line-height: 1.25;
                word-break: break-word;
            }
            .se-badge-notify__ok {
                width: 100%;
            }
            @keyframes se-badge-notify-fade-in {
                from {
                    opacity: 0;
                }
                to {
                    opacity: 1;
                }
            }
            @keyframes se-badge-notify-pop {
                from {
                    opacity: 0;
                    transform: translate(-50%, -46%) scale(0.92);
                }
                to {
                    opacity: 1;
                    transform: translate(-50%, -50%) scale(1);
                }
            }
            @keyframes se-badge-notify-shimmer {
                to {
                    background-position: 200% center;
                }
            }
            @keyframes se-badge-notify-card-glow {
                from {
                    box-shadow: 0 0 12px rgba(129, 140, 248, 0.2);
                }
                to {
                    box-shadow: 0 0 22px rgba(244, 114, 182, 0.35);
                }
            }
        `,
    ],
})
export class SeBadgeNotificationComponent {
    private readonly userStore = inject(UserStore);

    readonly labels = ENGAGEMENT_LABELS;

    readonly unacknowledged = s25Resource({
        if: () => this.userStore.loggedIn(),
        loader: () => EngagementService.getMyUnacknowledgedBadges(),
    });
    readonly badgeDefs = s25Resource({
        if: () => this.userStore.loggedIn(),
        loader: () => EngagementService.getBadges(),
    });

    readonly userDismissed = signal(false);

    readonly defById = computed((): Map<number, EngagementBadge> => {
        const list = this.badgeDefs.value() ?? [];
        return new Map(list.filter((b) => b.badgeId != null).map((b) => [b.badgeId!, b]));
    });

    readonly displayRows = computed((): EarnedRow[] => {
        const earned = this.unacknowledged.value() ?? [];
        const map = this.defById();
        return earned.map((e) => {
            const def = map.get(e.badgeId);
            const isSecret = def ? !def.isVisible : false;
            return {
                earned: e,
                def,
                isSecret,
                displayName: def?.name ?? String(e.badgeId),
            };
        });
    });

    readonly hasSecret = computed(() => this.displayRows().some((r) => r.isSecret));

    readonly headline = computed(() => {
        const rows = this.displayRows();
        if (rows.length === 1 && rows[0].isSecret) return "You discovered a secret badge!";
        if (this.hasSecret()) return "You earned badges — including a secret discovery!";
        return "You earned these badges!";
    });

    readonly secretAside = signal(false);

    readonly overlayOpen = computed(
        () => this.userStore.loggedIn() && !this.userDismissed() && !!this.unacknowledged.value()?.length,
    );

    constructor() {
        effect((onCleanup) => {
            if (this.userDismissed()) {
                return;
            }
            const list = this.unacknowledged.value();
            if (!list?.length) {
                return;
            }
            const ids = list.map((x) => x.badgeId);
            const tid = window.setTimeout(() => {
                void EngagementService.acknowledgeBadges(ids);
            }, 700);
            onCleanup(() => window.clearTimeout(tid));
        });
    }

    dismiss(): void {
        this.userDismissed.set(true);
    }
}
